import { Progress } from "@b2b/ui-components";
import { Check } from "lucide-react";
import {
  getPasswordStrength,
  type PasswordStrengthLevel,
} from "../utils/passwordStrength";

const LEVEL_COLORS: Record<PasswordStrengthLevel, string> = {
  weak: "bg-red-500",
  fair: "bg-orange-500",
  medium: "bg-amber-500",
  strong: "bg-green-500",
};

const CRITERIA: {
  key: keyof ReturnType<typeof getPasswordStrength>["checks"];
  label: string;
}[] = [
  { key: "length", label: "8+ characters" },
  { key: "upper", label: "Uppercase letter" },
  { key: "lower", label: "Lowercase letter" },
  { key: "number", label: "Number" },
  { key: "special", label: "Special character" },
];

interface PasswordStrengthMeterProps {
  password: string;
}

export default function PasswordStrengthMeter({
  password,
}: PasswordStrengthMeterProps) {
  if (password.length === 0) {
    return (
      <p className="text-sm text-gray-500">Enter a password to see strength</p>
    );
  }

  const { percent, level, checks } = getPasswordStrength(password);
  const color = LEVEL_COLORS[level];

  return (
    <div className="space-y-3">
      <div className="space-y-1.5">
        <Progress
          value={percent}
          color={color}
          className="h-2"
          aria-valuenow={percent}
          aria-valuemin={0}
          aria-valuemax={100}
          aria-label="Password strength"
        />
        <p className="text-xs text-gray-500 capitalize">Strength: {level}</p>
      </div>
      <ul className="flex flex-wrap gap-x-4 gap-y-1 text-sm" role="list">
        {CRITERIA.map(({ key, label }) => {
          const met = checks[key];
          return (
            <li
              key={key}
              className={`flex items-center gap-1.5 ${met ? "text-green-600" : "text-gray-400"}`}
            >
              {met ? (
                <Check className="w-4 h-4 flex-shrink-0" aria-hidden />
              ) : (
                <span
                  className="w-4 h-4 flex-shrink-0 inline-block rounded-full border border-gray-300"
                  aria-hidden
                />
              )}
              <span>{label}</span>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
